import { useMemo } from 'react';
import useContentControls, { useContentControlsLoaded } from './useContentControls';

// Mapping clé interne → clé features.<key> dans content_controls.
// Legacy : social, social_feed, social_posts, social_profiles,
// social_messaging (objets séparés — social_feed / social_posts sont les
// gates RÉELS du feed/composer côté backend `require_community_access`,
// on les garde inchangés pour rester aligné avec le backend).
const FLAG_KEYS = {
  socialEnabled: 'social',
  feedEnabled: 'social_feed',
  postsEnabled: 'social_posts',
  profilesEnabled: 'social_profiles',
  messagingEnabled: 'social_messaging',
};

// Fallback strict : aucun flag posé → public masqué, pas d'admin preview,
// pas de landing. Cohérent avec _social_feature_resolved côté backend.
// SEULE exception fail-closed inversé : moderation_required défaut TRUE
// (on ne désactive jamais la modération par omission).
const DEFAULT_FLAGS = {
  // — Existants (compat 100 %, ne pas renommer : lus par 4 pages) —
  socialEnabled: false,
  socialVisible: false,
  socialAdminPreviewEnabled: false,
  feedEnabled: false,
  postsEnabled: false,
  profilesEnabled: false,
  messagingEnabled: false,
  // — Sous-flags consolidés features.social.* (S1B) —
  menuEnabled: false,
  publicLandingEnabled: false,
  hermesEnabled: false,
  marketplaceEnabled: false,
  mediaUploadEnabled: false,
  postingEnabled: false,      // alias backend-aligné de postsEnabled (social_posts)
  moderationRequired: true,   // fail-closed inversé : absent → true
  // — État de chargement explicite (anti-race) —
  loaded: false,
};

function readFlag(features, key) {
  const cfg = features && typeof features === 'object' ? features[key] : null;
  if (!cfg || typeof cfg !== 'object') return false;
  return cfg.enabled === true;
}

function readSubFlag(features, key, sub) {
  const cfg = features && typeof features === 'object' ? features[key] : null;
  if (!cfg || typeof cfg !== 'object') return false;
  return cfg[sub] === true;
}

// Lecture inversée fail-closed : objet/clé absent → true (on garde la
// modération active par défaut, on ne la coupe que sur un explicite false).
function readSubFlagDefaultTrue(features, key, sub) {
  const cfg = features && typeof features === 'object' ? features[key] : null;
  if (!cfg || typeof cfg !== 'object') return true;
  return cfg[sub] !== false;
}

export function useSocialFlags() {
  const cc = useContentControls();
  const loaded = useContentControlsLoaded();
  return useMemo(() => {
    const features = (cc && cc.features) || {};
    const next = { ...DEFAULT_FLAGS };
    for (const [field, flagKey] of Object.entries(FLAG_KEYS)) {
      next[field] = readFlag(features, flagKey);
    }
    // Sous-flags du module social (objet features.social) :
    // visible (menus publics) + admin_preview_enabled (test admin module off).
    next.socialVisible = readSubFlag(features, 'social', 'visible');
    next.socialAdminPreviewEnabled = readSubFlag(
      features, 'social', 'admin_preview_enabled'
    );
    // Ajouts S1B (tous fail-closed à false sauf moderation_required à true).
    next.menuEnabled = readSubFlag(features, 'social', 'menu');
    next.publicLandingEnabled = readSubFlag(features, 'social', 'public_landing');
    next.hermesEnabled = readSubFlag(features, 'social', 'hermes_enabled');
    next.marketplaceEnabled = readSubFlag(features, 'social', 'marketplace_enabled');
    next.mediaUploadEnabled = readSubFlag(features, 'social', 'media_upload_enabled');
    next.moderationRequired = readSubFlagDefaultTrue(
      features, 'social', 'moderation_required'
    );
    // posting_enabled : on l'expose mais on le LIE au gate backend réel
    // (social_posts → postsEnabled), pour ne jamais désynchroniser l'UI du
    // backend qui, lui, n'enforce que social_posts.
    next.postingEnabled = next.postsEnabled;
    // État de chargement (le guard ne décide jamais avant que ce soit true).
    next.loaded = loaded;
    return next;
  }, [cc, loaded]);
}

/**
 * Helper : un utilisateur donné a-t-il droit aux surfaces admin social ?
 *
 * Public strict (Footer icônes, HomePage showcase, ArticlePage share) →
 * utiliser directement `socialEnabled`.
 *
 * Admin tools (EditorPage panel publish, DashboardPage tab, SettingsPage
 * onglets social/social-api, /dashboard/social) → utiliser
 * `isSocialVisibleForUser(flags, isAdmin)`.
 */
export function isSocialVisibleForUser(flags, isAdmin) {
  if (!flags) return false;
  if (flags.socialEnabled) return true;
  return Boolean(isAdmin && flags.socialAdminPreviewEnabled);
}

// ── Helpers de gating de la landing /social (S1B) ───────────────────────────
// Tous fail-closed. canViewLanding autorise la VITRINE (public_landing) et la
// preview admin même module fermé ; les autres exigent le module réellement
// ouvert (socialEnabled) — sinon les sections s'affichent en « teaser ».

// Landing /social visible ? Vitrine publique OU module ouvert OU admin preview.
export function canViewLanding(flags, isAdmin) {
  if (!flags) return false;
  if (flags.publicLandingEnabled || flags.socialEnabled) return true;
  return Boolean(isAdmin && flags.socialAdminPreviewEnabled);
}

// Alias sémantique (nom du plan S1A/S1B).
export const isSocialLandingVisible = canViewLanding;

// Feed communautaire réellement consultable (aligné gate backend social_feed).
export function canViewFeed(flags) {
  return Boolean(flags && flags.socialEnabled && flags.feedEnabled);
}

// Publication réellement possible (aligné gate backend social_posts).
export function canPost(flags) {
  return Boolean(flags && flags.socialEnabled && flags.postingEnabled);
}

// Agent Hermès réellement utilisable (sinon teaser « Bientôt »).
export function canUseHermes(flags) {
  return Boolean(flags && flags.socialEnabled && flags.hermesEnabled);
}

// Marketplace léger réellement utilisable (sinon teaser « Bientôt »).
export function canUseMarketplace(flags) {
  return Boolean(flags && flags.socialEnabled && flags.marketplaceEnabled);
}

// Upload média réellement autorisé (exige module + publication + flag média).
export function canUploadMedia(flags) {
  return Boolean(
    flags && flags.socialEnabled && flags.postingEnabled && flags.mediaUploadEnabled
  );
}

// Entrée « Social » dans les menus (Header/Sheet). Gatée par menu + visibilité.
export function isSocialMenuVisible(flags) {
  if (!flags) return false;
  return Boolean(flags.menuEnabled && (flags.socialVisible || flags.socialEnabled));
}

export default useSocialFlags;
